import React, { useEffect, useState, useRef } from 'react';
import { Play, Pause, Star } from 'lucide-react';
export function FeaturedTestimonial() {
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const audioRef = useRef<HTMLAudioElement>(null);
  const progressBarRef = useRef<HTMLInputElement>(null);
  const togglePlay = () => {
    if (audioRef.current) {
      if (isPlaying) {
        audioRef.current.pause();
      } else {
        audioRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };
  useEffect(() => {
    const audio = audioRef.current;
    if (audio) {
      const handleEnded = () => setIsPlaying(false);
      const handleTimeUpdate = () => setCurrentTime(audio.currentTime);
      const handleLoadedMetadata = () => setDuration(audio.duration);
      audio.addEventListener('ended', handleEnded);
      audio.addEventListener('timeupdate', handleTimeUpdate);
      audio.addEventListener('loadedmetadata', handleLoadedMetadata);
      return () => {
        audio.removeEventListener('ended', handleEnded);
        audio.removeEventListener('timeupdate', handleTimeUpdate);
        audio.removeEventListener('loadedmetadata', handleLoadedMetadata);
      };
    }
  }, []);
  const handleProgressChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const time = Number(e.target.value);
    setCurrentTime(time);
    if (audioRef.current) {
      audioRef.current.currentTime = time;
    }
  };
  const formatTime = (time: number) => {
    if (isNaN(time)) return '0:00';
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };
  return <section className="w-full py-14 px-4 bg-navy" data-id="element-47">
      <div className="max-w-4xl mx-auto" data-id="element-48">
        <div className="shimmer-border rounded-2xl p-1 bg-burgundy" data-id="element-49">
          <div className="bg-burgundy rounded-xl p-8 md:p-12 flex flex-col items-center text-center relative overflow-hidden" data-id="element-50">
            {/* Background texture for the card */}
            <div className="absolute inset-0 opacity-10 bg-[url('data:image/svg+xml,%3Csvg width=\'20\' height=\'20\' viewBox=\'0 0 20 20\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cg fill=\'%23C9A84C\' fill-opacity=\'1\' fill-rule=\'evenodd\'%3E%3Ccircle cx=\'3\' cy=\'3\' r=\'3\'/%3E%3Ccircle cx=\'13\' cy=\'13\' r=\'3\'/%3E%3C/g%3E%3C/svg%3E')]" data-id="element-51"></div>

            <div className="relative z-10 w-full flex flex-col items-center" data-id="element-52">
              <div className="inline-block bg-gold/20 border border-gold/50 text-gold text-xs font-bold tracking-widest uppercase px-4 py-1.5 rounded-full mb-8" data-id="element-53">
                ⭐ FEATURED STORY — REAL VOICE, REAL RESULT
              </div>

              <div className="flex items-center space-x-4 mb-8" data-id="element-54">
                <img src="https://cdn.magicpatterns.com/uploads/7UrKq4jNHPTP1BLEyLxG2z/ce2b7058-2f1c-48f8-bc67-aa174cbdeb92.png" alt="Rachel M." className="w-20 h-20 md:w-28 md:h-28 rounded-full object-cover border-2 border-gold/50 shadow-lg" data-id="element-55" />
                <div className="text-left" data-id="element-56">
                  <h3 className="font-serif text-2xl md:text-3xl text-cream font-bold" data-id="element-57">
                    Rachel M., 34
                  </h3>
                  <div className="flex items-center space-x-1.5 mt-1" data-id="element-58">
                    <svg className="w-4 h-4 text-blue-500 flex-shrink-0" viewBox="0 0 24 24" fill="currentColor" data-id="element-59">
                      <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" data-id="element-60" />
                    </svg>
                    <p className="text-cream/70 text-sm" data-id="element-61">@rachel_m_atx</p>
                  </div>
                  <p className="text-cream/50 text-sm mt-0.5" data-id="element-62">Austin, TX</p>
                </div>
              </div>

              <p className="font-serif italic text-2xl md:text-3xl text-cream/90 leading-relaxed max-w-3xl mb-12" data-id="element-63">
                "I didn't believe in any of this. I bought it at midnight
                because I was desperate. By Day 3, something shifted that I
                cannot explain rationally. The opportunity I had been waiting
                for for 8 months... showed up. Out of nowhere."
              </p>

              {/* WhatsApp-style Audio Player */}
              <div className="w-full max-w-md flex flex-col items-center mb-10" data-id="element-64">
                <p className="text-gold text-xs tracking-[0.2em] font-bold uppercase mb-4" data-id="element-65">
                  🎧 PRESS PLAY — HEAR RACHEL'S STORY
                </p>

                <div className="w-full bg-navy-dark/50 border border-gold/20 rounded-full p-2 pr-6 flex items-center space-x-4 shadow-lg backdrop-blur-sm" data-id="element-66">
                  {/* Play/Pause Button */}
                  <button onClick={togglePlay} className={`relative w-12 h-12 flex-shrink-0 rounded-full bg-gold flex items-center justify-center text-navy hover:scale-105 transition-transform duration-300 focus:outline-none ${!isPlaying ? 'animate-pulse-glow' : ''}`} aria-label={isPlaying ? 'Pause audio' : 'Play audio'} data-id="element-67">
                    {isPlaying ? <Pause className="w-5 h-5 fill-current" data-id="element-68" /> : <Play className="w-5 h-5 fill-current ml-1" data-id="element-69" />}
                  </button>

                  {/* Progress Bar & Time */}
                  <div className="flex-1 flex flex-col justify-center" data-id="element-70">
                    <div className="flex items-center space-x-3" data-id="element-71">
                      <input type="range" ref={progressBarRef} min={0} max={duration || 100} value={currentTime} onChange={handleProgressChange} className="w-full h-1.5 bg-gold/20 rounded-lg appearance-none cursor-pointer accent-gold focus:outline-none" style={{
                      background: `linear-gradient(to right, #C9A84C ${currentTime / (duration || 1) * 100}%, rgba(201, 168, 76, 0.2) ${currentTime / (duration || 1) * 100}%)`
                    }} data-id="element-72" />
                    </div>
                    <div className="flex justify-between mt-1.5 px-1" data-id="element-73">
                      <span className="text-cream/60 text-[10px] font-medium tracking-wider" data-id="element-74">
                        {formatTime(currentTime)}
                      </span>
                      <span className="text-cream/60 text-[10px] font-medium tracking-wider" data-id="element-75">
                        {duration ? formatTime(duration) : '1:58'}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Hidden native audio element (playing the audio track of the mp4) */}
                <audio ref={audioRef} src="https://cdn.magicpatterns.com/uploads/27Xqpr7a4Hpcn1LY1Af9Xs/202603181755.mp4" preload="metadata" data-id="element-76" />
              </div>

              <div className="flex flex-col items-center" data-id="element-77">
                <div className="flex space-x-1 mb-2" data-id="element-78">
                  {[...Array(5)].map((_, i) => <Star key={i} className="w-5 h-5 fill-gold text-gold" data-id="element-79" />)}
                </div>
                <p className="text-cream/60 text-sm font-medium" data-id="element-80">
                  Verified Purchase
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}